import React,{useMemo} from 'react';
import {defaultLineItems} from '../data/lineItemData';
import {
  calculateBaseTotalCents,
  lineItemTotalCents
} from '../services/quoteDraftService';
import './LineItems.css';

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-AU',{
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(cents / 100);
}

function sanitizeDecimal(value) {
  return value.replace(/[^\d.]/g,'').replace(/(\..*)\./g,'$1');
}

function LineItemRow({item,onChange,onRemove,disabled}) {
  const update=(field,value)=> onChange(item.id,field,value);

  return (
    <div className="line-item-editor-row">
      <input
        aria-label="Item title"
        disabled={disabled}
        onChange={(event)=> update('title',event.target.value)}
        placeholder="Item"
        value={item.title}
      />
      <input
        aria-label="Item description"
        disabled={disabled}
        onChange={(event)=> update('description',event.target.value)}
        placeholder="Description"
        value={item.description}
      />
      <input
        aria-label="Quantity or days"
        disabled={disabled}
        inputMode="decimal"
        min="0"
        onChange={(event)=> update('quantity',sanitizeDecimal(event.target.value))}
        step="any"
        type="text"
        value={item.quantity}
      />
      <div className="line-item-money">
        <span>$</span>
        <input
          aria-label="Unit price"
          disabled={disabled}
          inputMode="decimal"
          min="0"
          onChange={(event)=> update('unitPrice',sanitizeDecimal(event.target.value))}
          step="0.01"
          type="text"
          value={item.unitPrice}
        />
      </div>
      <strong>{formatCurrency(lineItemTotalCents(item))}</strong>
      <button
        aria-label={`Remove ${item.title || 'line item'}`}
        className="line-item-remove"
        disabled={disabled}
        onClick={()=> onRemove(item.id)}
        type="button"
      >
        ×
      </button>
    </div>
  );
}

function LineItemsEditor({items,onItemsChange,onTotalChange,disabled=false}) {
  const currentItems=items || defaultLineItems.map((item)=> ({
    ...item,
    quantity: String(item.quantity),
    unitPrice: String(item.unitPrice)
  }));

  const subtotalCents=useMemo(
    ()=> calculateBaseTotalCents(currentItems),
    [currentItems]
  );

  const gstCents=Math.round(subtotalCents * 0.1);
  const totalCents=subtotalCents + gstCents;

  React.useEffect(()=> {
    onTotalChange?.(subtotalCents);
  },[onTotalChange,subtotalCents]);

  const updateItem=(id,field,value)=> {
    if (disabled) {
      return;
    }

    onItemsChange((current)=> current.map((item)=> item.id===id
      ? {...item,[field]: value}
      : item
    ));
  };

  const removeItem=(id)=> {
    if (disabled) {
      return;
    }

    onItemsChange((current)=> current.filter((item)=> item.id !==id));
  };

  const addItem=()=> {
    if (disabled) {
      return;
    }

    onItemsChange((current)=> [
      ...current,
      {
        id: `custom-${Date.now()}`,
        title: '',
        description: '',
        quantity: '1',
        unitPrice: '0'
      }
    ]);
  };

  return (
    <section className="builder-section line-items-section">
      <div className="section-heading">
        <span className="eyebrow">Base Quotation</span>
        <div>
          <h2>Quote line items</h2>
          <p>Add any production cost without changing the quote structure.</p>
        </div>
      </div>

      <div className="line-item-editor">
        <div className="line-item-editor-head">
          <span>Item</span>
          <span>Description</span>
          <span>Qty / Days</span>
          <span>Unit Price</span>
          <span>Price</span>
          <span />
        </div>

        {currentItems.map((item)=> (
          <LineItemRow
            disabled={disabled}
            item={item}
            key={item.id}
            onChange={updateItem}
            onRemove={removeItem}
          />
        ))}
      </div>

      <div className="line-items-footer">
        <button
          className="text-button"
          disabled={disabled}
          onClick={addItem}
          type="button"
        >
          + ADD LINE ITEM
        </button>

        <div className="line-items-summary">
          <div>
            <span>Subtotal ex GST</span>
            <strong>{formatCurrency(subtotalCents)}</strong>
          </div>
          <div>
            <span>GST 10%</span>
            <strong>{formatCurrency(gstCents)}</strong>
          </div>
          <div className="line-items-summary-total">
            <span>Total including GST</span>
            <strong>{formatCurrency(totalCents)}</strong>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LineItemsEditor;